import React, { useMemo, useRef, useState } from 'react';
import { format, parse } from 'date-fns';
import { Box, Popover, TextField, InputAdornment, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { DateRangePicker, Range, RangeKeyDict } from 'react-date-range';
import { defaultStaticRanges } from 'react-date-range/dist/defaultRanges';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

export interface DateRangeFieldProps {
  label?: string;
  startDate?: string;
  endDate?: string;
  onChange: (start: string, end: string) => void;
  fullWidth?: boolean;
  errorText?: string;
}

export const DateRangeField: React.FC<DateRangeFieldProps> = ({
  label = 'Date range',
  startDate = '',
  endDate = '',
  onChange,
  fullWidth = true,
  errorText,
}) => {
  const anchorRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [tempRange, setTempRange] = useState<Range>({
    startDate: startDate ? parseDate(startDate) : undefined,
    endDate: endDate ? parseDate(endDate) : undefined,
    key: 'selection',
  });
  const [selectionStep, setSelectionStep] = useState(0);

  const displayText = useMemo(() => {
    const start = tempRange.startDate;
    const end = tempRange.endDate;
    if (!start && !end) return '';
    const fmt = (d: Date) => format(d, 'yyyy-MM-dd');
    return `${start ? fmt(start) : ''}${start || end ? ' – ' : ''}${end ? fmt(end) : ''}`;
  }, [tempRange]);

  const handleSelect = (range: RangeKeyDict['selection']) => {
    const { startDate, endDate } = range;

    // Always update tempRange for UI feedback
    setTempRange({ ...range });

    // Check if selection matches a preset
    const isPreset =
      startDate &&
      endDate &&
      defaultStaticRanges.some(
        (preset) =>
          preset.range().startDate?.toDateString() === startDate.toDateString() &&
          preset.range().endDate?.toDateString() === endDate.toDateString(),
      );

    if (isPreset) {
      // Immediately apply for preset and reset step logic
      onChange(formatDate(startDate), formatDate(endDate));
      setOpen(false);
      setSelectionStep(0);
      return;
    }

    // If this is the first click, just move to step 1
    if (selectionStep === 0) {
      setSelectionStep(1);
      // No API call yet
      return;
    }

    // On the second selection, call API (even for same date)
    if (selectionStep === 1) {
      setSelectionStep(0); // Ready for next range selection

      onChange(formatDate(startDate), formatDate(endDate));
      setOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTempRange({ startDate: undefined, endDate: undefined, key: 'selection' });
    setSelectionStep(0);
    onChange('', '');
    setOpen(false);
  };

  return (
    <>
      <TextField
        inputRef={anchorRef}
        label={label}
        value={displayText}
        onClick={() => setOpen(true)}
        fullWidth={fullWidth}
        slotProps={{
          inputLabel: {
            sx: {
              color: (theme: any) => theme.palette.text.secondary,
            },
          },
          input: {
            readOnly: true,
            style: { cursor: 'pointer' },
            endAdornment:
              startDate || endDate ? (
                <InputAdornment position="end">
                  <IconButton size="small" onMouseDown={(e) => e.stopPropagation()} onClick={handleClear}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
          },
        }}
        error={!!errorText}
        helperText={errorText}
      />

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: (theme) => theme.palette.background.paper,
              color: (theme) => theme.palette.text.primary,
              boxShadow: (theme) => theme.shadows[6],
              borderRadius: 2,

              // Calendar wrapper
              '.rdrCalendarWrapper,.rdrDateDisplayWrapper,.rdrDateInput': {
                bgcolor: (theme) => theme.palette.background.paper,
                color: (theme) => theme.palette.text.primary,
              },

              // Preset range list (left panel)
              '.rdrDefinedRangesWrapper': {
                bgcolor: (theme) => theme.palette.background.default,
                borderRight: (theme) => `1px solid ${theme.palette.divider}`,
              },

              // Each preset item
              '.rdrStaticRange': {
                bgcolor: 'transparent',
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
                '.rdrStaticRangeLabel': {
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  color: (theme) => theme.palette.text.primary,
                },
              },
              '.rdrStaticRangeSelected': {
                bgcolor: (theme) => theme.palette.action.selected,
              },

              // Input range area
              '.rdrInputRangeInput': {
                bgcolor: (theme) => theme.palette.background.default,
                color: (theme) => theme.palette.text.primary,
                borderColor: (theme) => theme.palette.divider,
              },

              // Day cells
              '.rdrDayNumber span': {
                color: (theme) => theme.palette.text.primary,
              },
              '.rdrDayDisabled, .rdrDayPassive span': {
                color: (theme) => theme.palette.text.disabled,
              },

              // Month/year selectors
              '.rdrMonthAndYearPickers select,.rdrMonthAndYearPickers option,': {
                bgcolor: (theme) => theme.palette.background.default,
                color: (theme) => theme.palette.text.primary,
              },
            },
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <DateRangePicker
            ranges={[tempRange]}
            onChange={(item) => handleSelect(item.selection)}
            moveRangeOnFirstSelection={false}
            editableDateInputs
            months={2}
            direction="horizontal"
            rangeColors={['#1976d2']}
            staticRanges={defaultStaticRanges}
            inputRanges={[]}
            startDatePlaceholder="Start Date"
            endDatePlaceholder="End Date"
          />
        </Box>
      </Popover>
    </>
  );
};

// Helpers
export const formatDate = (date: Date | string): string => {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
};

export const parseDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  return parse(dateString, 'yyyy-MM-dd', new Date());
};
